// What the Home (Console D, views/home.js; APS-305) derives: the list of what needs a person,
// ordered by impact on one scale, and the verdict that heads the page.
import type { StatusKey } from '../components/d'

/** What a row of "Needs you" is about. */
export type NeedKind = 'alert' | 'issue' | 'endpoint' | 'symbols' | 'review'

export interface Need {
  key: string
  kind: NeedKind
  status: StatusKey
  /** The kind word: crash, hang, anr, error, network, symbols, review, alert. */
  word: string
  /** The status word when the row's own differs from the status's (e.g. review). */
  statusLabel?: string | undefined
  title: string
  sub: string
  /**
   * Sessions of the release affected, per 1,000 of its sessions; undefined when the row has no
   * such measure (an alert on issues), and then it draws no bar.
   */
  perK: number | undefined
  /** The row's last days (oldest first), for its sparkline. */
  spark?: readonly number[] | undefined
  /** The two figures on the right: the main one and the one under it. */
  figure: [string, string]
}

/** Each row's bar as a share of the scale (0..1) and whether it runs off it. */
export interface Bar {
  share: number
  over: boolean
}

/**
 * One scale for every row, so bars compare (Mosaic, APS-305): the largest row that is not an
 * alert is the full bar. An alert beyond it draws a full bar with a break ("off the scale").
 */
export function bars(needs: readonly Need[]): Map<string, Bar | undefined> {
  const scale = Math.max(
    0,
    ...needs.filter((n) => n.kind !== 'alert' && n.perK !== undefined).map((n) => n.perK as number),
  )
  const out = new Map<string, Bar | undefined>()
  for (const n of needs) {
    if (n.perK === undefined) out.set(n.key, undefined)
    else if (scale <= 0) out.set(n.key, { share: n.perK > 0 ? 1 : 0, over: n.perK > 0 })
    else out.set(n.key, { share: Math.min(1, n.perK / scale), over: n.perK > scale })
  }
  return out
}

/** The list's order: most sessions affected per 1k first; rows without a measure last. */
export function byImpact(needs: readonly Need[]): Need[] {
  return [...needs].sort((a, b) => (b.perK ?? -1) - (a.perK ?? -1) || a.key.localeCompare(b.key))
}

/** Sessions per 1,000 of the release's: `count` of `total`. */
export function perThousand(count: number, total: number): number | undefined {
  return total > 0 ? (count / total) * 1000 : undefined
}
